/**
 * dp-configurator-curtain.js — Ruhiger Ladezustand über dem Konfigurator (QA 2026-09-17, F-14).
 *
 * Der DP-Connector-App-Block startet mit Fremdwerten („Quadrat 50×50 / glänzend / silber 23,57 €")
 * und braucht 7–30 s, bis die Brücke (dp-connector-bridge.js) das gewählte Format eingestellt hat.
 * Kommt die Produktseite mit Format-Kontext — `?fmt=` (Startseiten-Kachel, Megamenü), gespeicherte
 * Kachel-Auswahl sessionStorage['dp:selectedFormat'] oder `?design=` aus dem Designer —, legt dieses
 * Modul einen Vorhang in Theme-Optik über den App-Block: Spinner, Text mit den Werten der Auswahl
 * („Wir stellen dein Format ein – DIN A4 hoch, 170 g matt, schwarze Bindung"), Skeleton-Zeilen und
 * ein Fortschrittshinweis. Ohne Format-Kontext (direkter Aufruf) passiert nichts.
 *
 * Ende des Vorhangs (Zustandsautomat createCurtainState, ohne DOM getestet):
 *   · Format-Kontext: `dp:bridge-preselect` mit ok=true für den Schlüssel der Kachel-Auswahl
 *     (die Kacheln melden ihren Schlüssel per `dp:format-change`; ältere Vorauswahl-Ergebnisse
 *     für andere Schlüssel oder mit noch wartender Anfrage zählen nicht) → weich ausblenden.
 *   · `?design=`: der Dialog der Brücke deckt den Konfigurator — `dp:bridge-dialog` beendet den
 *     Vorhang; scheitert die Übergabe vorher (window.dpHandoff.result.ok === false), ebenfalls.
 *   · ok=false, Fehlerkarte des Mantels (dp-connector-band--stalled) oder Timeout (45 s): der Vorhang
 *     geht weg und ein kurzer Hinweis „Bitte Format unten prüfen …" bleibt über dem Block. Nie hängen.
 *
 * Barrierefreiheit: aria-busy am App-Block (.dp-main), Vorhang aria-hidden, Block `inert` solange
 * der Vorhang liegt (Fallback ohne inert: aria-hidden am Block); eine unsichtbare Statuszeile sagt
 * Start und Ende an. Der Vorhang liegt absolut über dem Slot (Mindesthöhe per CSS), kein Layoutsprung.
 * Ereignisse und CSS-Klassen: sections/dp-product-main.liquid (Konfigurator-Band).
 */

export const VERSION = '0.1.0';
export const TIMEOUT_MS = 45000;
export const SLOW_HINT_MS = 30000;
export const TICK_MS = 500;
export const FADE_MS = 400;
export const STORAGE_KEY = 'dp:selectedFormat';
export const FORMAT_EVENT = 'dp:format-change';
export const PRESELECT_EVENT = 'dp:bridge-preselect';
export const DIALOG_EVENT = 'dp:bridge-dialog';
export const STALLED_CLASS = 'dp-connector-band--stalled';
export const DESIGN_PARAMS = Object.freeze(['design', 'dp_design']);
const DESIGN_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
const FORMAT_KEY_PATTERN = /^[a-z0-9_]{3,64}$/;

export const TEXT = Object.freeze({
  format: (summary) => (summary ? `Wir stellen dein Format ein – ${summary}` : 'Wir stellen dein Format ein …'),
  design: 'Wir übernehmen deinen Kalender ins Bestellformular …',
  hint: 'Das dauert je nach Verbindung 10–30 Sekunden.',
  slow: 'Das dauert gerade etwas länger – wir warten noch kurz.',
  done: 'Format eingestellt.',
  check: 'Bitte Format unten prüfen – die automatische Einstellung hat nicht geklappt.',
});

// ── Reine Helfer ────────────────────────────────────────────────────────────

/** Gespeicherte Kachel-Auswahl (dp-format-picker.js schreibt event.detail als JSON). */
export function parseStoredFormat(raw) {
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && value.attrs && typeof value.attrs === 'object' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Format-Kontext der Seite: `?design=`/`?dp_design=` (Designer-Übergabe) vor `?fmt=` vor
 * gespeicherter Kachel-Auswahl. Ohne Kontext mode=null → kein Vorhang.
 */
export function readContext(search, storedRaw = null) {
  let params;
  try {
    const raw = String(search ?? '');
    params = new URLSearchParams((raw.includes('?') ? raw.slice(raw.indexOf('?') + 1) : raw).split('#')[0]);
  } catch {
    params = new URLSearchParams();
  }
  for (const param of DESIGN_PARAMS) {
    const value = params.get(param);
    if (value != null && DESIGN_ID_PATTERN.test(value)) return { mode: 'design', designId: value, formatKey: null, stored: null };
  }
  const fmt = String(params.get('fmt') ?? '').trim().toLowerCase();
  const stored = parseStoredFormat(storedRaw);
  if (FORMAT_KEY_PATTERN.test(fmt)) return { mode: 'format', designId: null, formatKey: fmt, stored: stored?.key === fmt ? stored : null };
  if (stored) return { mode: 'format', designId: null, formatKey: typeof stored.key === 'string' ? stored.key : null, stored };
  return { mode: null, designId: null, formatKey: null, stored: null };
}

const normName = (v) => String(v ?? '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
function attrValue(attrs, name) {
  const hit = Object.entries(attrs ?? {}).find(([key]) => normName(key) === normName(name));
  return hit ? String(hit[1] ?? '').trim() : '';
}

/** „170 g Bilderdruck matt PEFC" → „170 g matt"; Unbekanntes bleibt (ohne Sortenwort/Zertifikat). */
export function paperLabel(material) {
  return String(material ?? '')
    .replace(/\bBilderdruck\b/gi, '')
    .replace(/\s+(PEFC|FSC)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** „schwarz" → „schwarze Bindung"; „silber" → „silberne Bindung"; „weiß" → „weiße Bindung". */
export function bindingLabel(color) {
  const c = String(color ?? '').trim();
  if (!c) return '';
  const key = c.toLowerCase().replace(/ß/g, 'ss');
  if (key === 'schwarz') return 'schwarze Bindung';
  if (key === 'silber') return 'silberne Bindung';
  if (key === 'weiss') return 'weiße Bindung';
  return `Bindung ${c}`;
}

/** „DIN A4 hoch, 170 g matt, schwarze Bindung" aus dem Kachel-Detail (name/label/attrs). */
export function describeSelection(detail) {
  if (!detail || typeof detail !== 'object') return '';
  const attrs = detail.attrs && typeof detail.attrs === 'object' ? detail.attrs : {};
  const name = String(detail.name ?? detail.label ?? attrValue(attrs, 'Format')).trim();
  return [name, paperLabel(attrValue(attrs, 'Material')), bindingLabel(attrValue(attrs, 'Farbe der Wire-O-Bindung'))].filter(Boolean).join(', ');
}

/**
 * Zustandsautomat: waiting → done | failed | timeout.
 *   mode 'format': preselect(ok, key, pending) entscheidet — nur für den erwarteten Schlüssel
 *     (wantedKey). awaitKey=true (Kacheln auf der Seite, noch nicht gerendert) hält jedes Ergebnis
 *     zurück, bis formatChange den Schlüssel der Kacheln liefert — die Kacheln melden beim ersten
 *     Rendern immer ihre Vorauswahl, und erst die zählt.
 *   mode 'design': preselect zählt nicht; dialog() oder handoffResult() beenden.
 *   stalled() (Fehlerkarte des Mantels) → failed; tick(now) nach timeoutMs → timeout.
 * Nach dem ersten Endzustand ändert sich nichts mehr.
 */
export function createCurtainState({ mode = 'format', wantedKey = null, awaitKey = false, timeoutMs = TIMEOUT_MS, startedAt = 0 } = {}) {
  const s = { state: 'waiting', reason: null, mode, wantedKey: wantedKey != null ? String(wantedKey) : null, awaitKey: Boolean(awaitKey) };
  const settle = (state, reason) => {
    if (s.state === 'waiting') { s.state = state; s.reason = reason; }
    return s.state;
  };
  return {
    get state() { return s.state; },
    get reason() { return s.reason; },
    get mode() { return s.mode; },
    get wantedKey() { return s.wantedKey; },
    formatChange(detail) {
      if (detail?.key != null && detail.key !== '') { s.wantedKey = String(detail.key); s.awaitKey = false; }
      return s.state;
    },
    preselect({ ok = false, key = null, pending = false, reason = null } = {}) {
      if (s.state !== 'waiting' || s.mode === 'design') return s.state;
      if (pending) return s.state; // neuere Anfrage der Brücke folgt
      if (s.awaitKey) return s.state; // Kacheln haben ihren Schlüssel noch nicht gemeldet (formatChange)
      if (s.wantedKey && key != null && String(key) !== s.wantedKey) return s.state; // altes Ergebnis
      return settle(ok ? 'done' : 'failed', ok ? 'preselect' : `preselect:${reason ?? 'failed'}`);
    },
    dialog() { return settle('done', 'dialog'); },
    handoffResult(result) {
      if (!result || typeof result !== 'object') return s.state;
      return settle(result.ok ? 'done' : 'failed', `handoff:${result.step ?? (result.ok ? 'ok' : 'failed')}`);
    },
    stalled() { return settle('failed', 'stalled'); },
    tick(now) {
      if (now - startedAt >= timeoutMs) settle('timeout', 'timeout');
      return s.state;
    },
  };
}

// ── DOM (mit fake-dom testbar) ───────────────────────────────────────────────
function el(doc, tag, className, text) {
  const node = doc.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * Legt den Vorhang in den Slot, sperrt den Block; liefert setTitle/setHint/finish.
 * finish('done') blendet weich aus; finish('failed'|'timeout') zusätzlich mit Hinweis über dem Block.
 */
export function mountCurtain({ slot, block, title, hint = TEXT.hint, doc = globalThis.document, timers = globalThis, fadeMs = FADE_MS } = {}) {
  if (!slot || !block) return null;
  const curtain = el(doc, 'div', 'dp-curtain');
  curtain.setAttribute('aria-hidden', 'true');
  curtain.setAttribute('data-dp-curtain-el', '');
  const head = el(doc, 'div', 'dp-curtain__head');
  const spinner = el(doc, 'span', 'dp-curtain__spinner');
  const textBox = el(doc, 'div', 'dp-curtain__text');
  const titleNode = el(doc, 'p', 'dp-curtain__title', title);
  const hintNode = el(doc, 'p', 'dp-curtain__hint', hint);
  textBox.append(titleNode, hintNode);
  head.append(spinner, textBox);
  const skeleton = el(doc, 'div', 'dp-curtain__skeleton');
  for (let i = 0; i < 4; i += 1) skeleton.append(el(doc, 'span', 'dp-curtain__bar'));
  curtain.append(head, skeleton);
  const live = el(doc, 'p', 'dp-curtain__live', title);
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');
  slot.append(curtain, live);
  slot.setAttribute('data-dp-curtain', 'waiting');
  block.setAttribute('aria-busy', 'true');
  const hasInert = 'inert' in block;
  if (hasInert) block.inert = true;
  else block.setAttribute('aria-hidden', 'true');
  block.setAttribute('data-dp-curtain-locked', hasInert ? 'inert' : 'aria-hidden');
  let finished = false;
  const release = () => {
    block.setAttribute('aria-busy', 'false');
    if (hasInert) block.inert = false;
    else block.removeAttribute('aria-hidden');
    block.removeAttribute('data-dp-curtain-locked');
  };
  return {
    element: curtain,
    setTitle(text) { titleNode.textContent = text; live.textContent = text; },
    setHint(text) { hintNode.textContent = text; },
    finish(state, reason = null) {
      if (finished) return;
      finished = true;
      const ok = state === 'done';
      release(); // Tastatur/Screenreader sofort frei, der Vorhang blendet nur noch aus
      slot.setAttribute('data-dp-curtain', ok ? 'done' : 'failed');
      curtain.classList.add('dp-curtain--leaving');
      if (reason) curtain.setAttribute('data-dp-curtain-reason', reason);
      if (ok) {
        live.textContent = reason === 'dialog' ? '' : TEXT.done;
      } else {
        live.textContent = '';
        const note = el(doc, 'p', 'dp-curtain__note', TEXT.check);
        note.setAttribute('role', 'status');
        note.setAttribute('data-dp-curtain-note', reason ?? state);
        slot.prepend(note);
      }
      timers.setTimeout(() => {
        curtain.remove();
        slot.removeAttribute('data-dp-curtain');
      }, fadeMs);
    },
  };
}

/** Status der Brücke (window.dpBridge.status ist ein Objekt; tolerant gegenüber einer Funktion). */
const readBridgeStatus = (win) => {
  const bridge = win?.dpBridge;
  return (typeof bridge?.status === 'function' ? bridge.status() : bridge?.status) ?? null;
};

/**
 * Start: Kontext lesen, Vorhang legen, Ereignisse verdrahten. Liefert null ohne Kontext/Slot/Block.
 * doc/win/timers/now sind für Tests injizierbar.
 */
export function startCurtain({ doc = globalThis.document, win = globalThis.window, timers = globalThis, now = Date.now, timeoutMs = TIMEOUT_MS, tickMs = TICK_MS } = {}) {
  let storedRaw = null;
  try { storedRaw = win?.sessionStorage?.getItem(STORAGE_KEY) ?? null; } catch { storedRaw = null; }
  const ctx = readContext(win?.location?.search ?? '', storedRaw);
  if (!ctx.mode) return null;
  const slot = doc.querySelector('.dp-product-main__app-slot');
  const block = slot?.querySelector('.dp-main');
  if (!slot || !block || slot.querySelector('[data-dp-curtain-el]')) return null;
  const picker = doc.querySelector('dp-format-picker');
  const selection = picker?.selection ?? ctx.stored ?? null;
  const startedAt = now();
  const machine = createCurtainState({
    mode: ctx.mode,
    wantedKey: selection?.key ?? ctx.formatKey ?? null,
    awaitKey: Boolean(picker) && !picker?.selection,
    timeoutMs,
    startedAt,
  });
  const title = ctx.mode === 'design' ? TEXT.design : TEXT.format(describeSelection(selection));
  const ui = mountCurtain({ slot, block, title, doc, timers });
  if (!ui) return null;
  let interval = 0;
  let slowShown = false;
  const stop = () => {
    timers.clearInterval(interval);
    doc.removeEventListener?.(FORMAT_EVENT, onFormat);
    doc.removeEventListener?.(PRESELECT_EVENT, onPreselect);
    doc.removeEventListener?.(DIALOG_EVENT, onDialog);
    ui.finish(machine.state, machine.reason);
  };
  const check = () => { if (machine.state !== 'waiting') stop(); };
  const onFormat = (event) => {
    machine.formatChange(event?.detail);
    if (ctx.mode === 'format') ui.setTitle(TEXT.format(describeSelection(event?.detail)));
  };
  const onPreselect = (event) => {
    const d = event?.detail ?? {};
    machine.preselect({ ok: d.ok === true, key: d.key ?? null, reason: d.reason ?? null, pending: Boolean(readBridgeStatus(win)?.preselect?.pending) });
    check();
  };
  const onDialog = () => { machine.dialog(); check(); };
  doc.addEventListener(FORMAT_EVENT, onFormat);
  doc.addEventListener(PRESELECT_EVENT, onPreselect);
  doc.addEventListener(DIALOG_EVENT, onDialog);
  interval = timers.setInterval(() => {
    const t = now();
    if (slot.classList.contains(STALLED_CLASS)) machine.stalled();
    else if (ctx.mode === 'design' && win?.dpHandoff?.result) machine.handoffResult(win.dpHandoff.result);
    else machine.tick(t);
    if (!slowShown && machine.state === 'waiting' && t - startedAt >= SLOW_HINT_MS) { slowShown = true; ui.setHint(TEXT.slow); }
    check();
  }, tickMs);
  return { machine, ui, stop, mode: ctx.mode };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const boot = () => { try { window.dpCurtain = startCurtain(); } catch (error) { console.warn('[dp-curtain]', error); } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
